// Metric catalogue shared by KPI cards, the trend panel, the leaderboard and
// the CSV export. `aggregate` says how a period total is derived:
//   'sum'     -> add the buckets of a time series (plays, watch time)
//   'max'     -> peak bucket (concurrent viewers)
//   'total'   -> needs its own no-interval request (unique viewers)
//   'derived' -> computed from other metrics, never requested (avg view time)

export const METRICS = [
	{
		key: 'play',
		label: 'Plays',
		shortLabel: 'Plays',
		aggregate: 'sum',
		unit: 'count',
		definition: 'A play is counted each time a viewer starts playback in the Castr player.',
	},
	{
		key: 'uniqueUsers',
		label: 'Unique viewers',
		shortLabel: 'Unique',
		aggregate: 'total',
		unit: 'count',
		definition: 'Distinct browsers or devices that started playback at least once in the period.',
	},
	{
		key: 'totalHoursWatched',
		label: 'Watch time',
		shortLabel: 'Watch time',
		aggregate: 'sum',
		unit: 'duration',
		definition: 'Total time viewers spent playing your content, added up across every play.',
	},
	{
		// Bitmovin's VIEWTIME average is per analytics sample, not per session,
		// so it reads as a few seconds. Watch time / plays is what people mean.
		key: 'avgViewTime',
		label: 'Avg. view time',
		shortLabel: 'Avg. view',
		aggregate: 'derived',
		derivedFrom: { numerator: 'totalHoursWatched', denominator: 'play' },
		unit: 'duration',
		definition: 'Watch time divided by plays for the selected period.',
	},
	{
		key: 'concurrentViewers',
		label: 'Peak concurrent viewers',
		shortLabel: 'Peak viewers',
		trendLabel: 'Concurrent viewers',
		aggregate: 'max',
		unit: 'count',
		definition: 'The highest number of viewers watching at the same time, measured per minute.',
	},
];

export const METRIC_BY_KEY = Object.fromEntries(METRICS.map((m) => [m.key, m]));

export const DEFAULT_TREND_METRIC = 'play';

/**
 * Metrics that have to be fetched to show `metricKey` (derived metrics expand
 * to their inputs, everything else to itself).
 */
export function metricDependencies(metricKey) {
	const def = METRIC_BY_KEY[metricKey];
	if (def?.aggregate === 'derived') return [def.derivedFrom.numerator, def.derivedFrom.denominator];
	return [metricKey];
}

/**
 * Average view time in ms = watch time (ms) / plays. Null when there are no
 * plays or either input is missing, so callers can show a placeholder instead
 * of a misleading "0m 00s".
 */
export function avgViewTimeMs(watchTimeMs, plays) {
	const w = Number(watchTimeMs);
	const p = Number(plays);
	if (watchTimeMs === null || watchTimeMs === undefined || plays === null || plays === undefined) return null;
	if (!Number.isFinite(w) || !Number.isFinite(p) || p <= 0) return null;
	return w / p;
}

/**
 * Per-bucket average view time from two aligned [[ts, value]] series. Buckets
 * are matched by timestamp; a bucket without plays yields 0 so the chart stays
 * continuous.
 */
export function deriveAvgViewTimeSeries(watchSeries, playSeries) {
	if (!Array.isArray(watchSeries) || !Array.isArray(playSeries)) return [];
	const plays = new Map(playSeries.map(([ts, v]) => [ts, v]));
	return watchSeries.map(([ts, watch]) => {
		const avg = avgViewTimeMs(watch, plays.get(ts));
		return [ts, avg === null ? 0 : avg];
	});
}

export const AUDIENCE_TABS = [
	{ key: 'country', label: 'Country', groupBy: 'COUNTRY', type: 'play' },
	{ key: 'region', label: 'Region', groupBy: 'REGION', type: 'play' },
	{ key: 'device', label: 'Device', groupBy: 'DEVICE_CLASS', type: 'play' },
	{ key: 'browser', label: 'Browser', groupBy: 'BROWSER', type: 'play' },
	{ key: 'os', label: 'OS', groupBy: 'OPERATINGSYSTEM', type: 'play' },
	// `optional`: plays can exist without any captions/audio-track usage, so an
	// empty breakdown here is a real empty state, not a failed request.
	{ key: 'captions', label: 'Captions', groupBy: 'SUBTITLE_LANGUAGE', type: 'subtitles', emptyLabel: 'Off', optional: true, emptyTitle: 'No caption data for this selection', emptyText: 'No plays with captions were recorded for the selected content and dates.' },
	{ key: 'audio', label: 'Audio tracks', groupBy: 'AUDIO_LANGUAGE', type: 'audio', emptyLabel: 'Default', optional: true, emptyTitle: 'No audio track data for this selection', emptyText: 'No plays with a selected audio track were recorded for the selected content and dates.' },
];

export const AUDIENCE_TAB_BY_KEY = Object.fromEntries(AUDIENCE_TABS.map((t) => [t.key, t]));

export const SCOPES = [
	{ key: 'all', label: 'All' },
	{ key: 'live', label: 'Live streams' },
	{ key: 'vod', label: 'Videos' },
];

export const MAX_SELECTED_CONTENT = 10;

export const LEADERBOARD_COLUMNS = [
	{ key: 'play', label: 'Plays', metric: 'play' },
	{ key: 'uniqueUsers', label: 'Unique viewers', metric: 'uniqueUsers' },
	{ key: 'totalHoursWatched', label: 'Watch time', metric: 'totalHoursWatched' },
	{ key: 'avgViewTime', label: 'Avg. view time', metric: 'avgViewTime' },
	{ key: 'concurrentViewers', label: 'Peak viewers', metric: 'concurrentViewers' },
];

export const LEADERBOARD_PRIMARY_COLUMN = 'totalHoursWatched';

// Columns that cost a grouped request each; derived columns are computed from
// these once their inputs are present.
export const LEADERBOARD_REQUEST_COLUMNS = Array.from(new Set(LEADERBOARD_COLUMNS.flatMap((c) => metricDependencies(c.metric))));

/**
 * Add derived columns (avg view time) to a row's fetched metrics.
 */
export function withDerivedMetrics(metrics = {}) {
	const out = { ...metrics };
	for (const m of METRICS) {
		if (m.aggregate !== 'derived') continue;
		const { numerator, denominator } = m.derivedFrom;
		if (out[numerator] !== undefined && out[denominator] !== undefined) {
			out[m.key] = avgViewTimeMs(out[numerator], out[denominator]);
		}
	}
	return out;
}

/**
 * Sort leaderboard rows by a numeric column; missing values sink to the end.
 */
export function sortLeaderboard(rows, column, direction = 'desc') {
	const dir = direction === 'asc' ? 1 : -1;
	return rows.slice().sort((a, b) => {
		const av = a.metrics?.[column];
		const bv = b.metrics?.[column];
		const aMissing = av === null || av === undefined;
		const bMissing = bv === null || bv === undefined;
		if (aMissing && bMissing) return a.name.localeCompare(b.name);
		if (aMissing) return 1;
		if (bMissing) return -1;
		if (av === bv) return a.name.localeCompare(b.name);
		return (av - bv) * dir;
	});
}

/**
 * Collapse grouped rows to top N + "Other" and compute shares.
 */
export function topWithOther(rows, n = 8, otherLabel = 'Other') {
	const total = rows.reduce((acc, r) => acc + r.value, 0);
	const top = rows.slice(0, n).map((r) => ({ ...r, share: total ? r.value / total : 0 }));
	const rest = rows.slice(n);
	if (rest.length) {
		const otherValue = rest.reduce((acc, r) => acc + r.value, 0);
		top.push({ key: '__other__', label: otherLabel, value: otherValue, share: total ? otherValue / total : 0, isOther: true, count: rest.length });
	}
	return { rows: top, total };
}
